import { type ReactNode, useState } from 'react';
import type { Key } from 'react-aria-components';
import type { SelectProps } from '@launchpad-ui/components';
import { ListBox, Select, Text } from '@launchpad-ui/components';

import { useFieldContext } from '../../contexts';

import { FormFieldError } from './FormFieldError';
import { InlineSelectPopover } from './InlineSelectPopover';
import { InlineSelectTrigger } from './InlineSelectTrigger';

import styles from './Select.module.css';

export interface InlineSelectProps<T extends object> extends Omit<
  SelectProps<T>,
  'children' | 'onSelectionChange' | 'items'
> {
  description?: ReactNode;
  items?: Iterable<T>;
  children: React.ReactNode | ((item: T) => React.ReactNode);
  listFooter?: ReactNode;
  onSelectionChange?: (key: string) => void;
  emptyTrigger?: ReactNode;
  selectedTrigger?: (selectedItem: T) => ReactNode;
  renderPopover?: (props: { children: ReactNode }) => ReactNode;
}

export function InlineSelect<T extends object>({
  description,
  items,
  children,
  listFooter,
  onSelectionChange,
  emptyTrigger,
  selectedTrigger,
  renderPopover,
  ...props
}: InlineSelectProps<T>) {
  const field = useFieldContext<string>();
  const [isOpen, setIsOpen] = useState(false);

  // Find selected item
  const selectedItem = items
    ? Array.from(items).find((item: T) => {
        const itemKey =
          'key' in item && typeof item.key === 'string'
            ? item.key
            : 'id' in item && typeof item.id === 'string'
              ? item.id
              : '';
        return itemKey === field.state.value;
      }) || null
    : null;

  const hasSelection = Boolean(field.state.value && selectedItem);

  const handleSelectionChange = (selectedKey: Key | null) => {
    if (props.isDisabled || selectedKey === null) {
      return;
    }

    const keyString = typeof selectedKey === 'string' ? selectedKey : String(selectedKey);
    field.handleChange(keyString);
    if (onSelectionChange) {
      onSelectionChange(keyString);
    }
    // Close the popover after selection
    setIsOpen(false);
  };

  const popoverElement = renderPopover || InlineSelectPopover;

  const fallbackAriaLabel =
    hasSelection && selectedItem && 'name' in selectedItem && typeof selectedItem.name === 'string'
      ? selectedItem.name
      : 'Select option';

  return (
    <div>
      <Select
        aria-label={fallbackAriaLabel}
        {...props}
        defaultValue={field.state.value || undefined}
        onChange={handleSelectionChange}
        onOpenChange={(open) => setIsOpen(open)}
        isOpen={isOpen}
      >
        <InlineSelectTrigger
          hasSelection={hasSelection}
          selectedItem={selectedItem}
          emptyTrigger={emptyTrigger}
          selectedTrigger={selectedTrigger}
        />
        {popoverElement({
          children: (
            <>
              <ListBox items={items} selectionMode="single" selectedKeys={field.state.value ? [field.state.value] : []}>
                {children}
              </ListBox>
              {listFooter && <div className={styles.listFooter}>{listFooter}</div>}
            </>
          ),
        })}
      </Select>
      {description && <Text>{description}</Text>}
      <FormFieldError meta={field.state.meta} />
    </div>
  );
}
